import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useApiClient from '../../hooks/api/useApiClient';
import usePermissions from '../../hooks/auth/usePermissions';
import useHistory from '../../hooks/navigation/useHistory';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import { PayoutRequirement, describePayoutRequirements } from '../../services/tenant/payoutRequirements';

/**
 * Reminds a platform-custody partner's admin, on every page, that their revenue
 * share is held until the Stripe payout account is transfer-ready. Hidden on
 * the billing page itself, which carries the full onboarding card; the state is
 * re-read on leaving it, which is where the partner comes back from Stripe.
 */
export default function PartnerPayoutAccountBanner() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Layout:PartnerPayoutAccountBanner' });
  const { PartnerBillingManager } = useApiClient();
  const { canAccessScreen } = usePermissions();
  const history = useHistory();
  const { pathname } = useLocation();
  const [payoutState, setPayoutState] = useState(null);

  const canSee = canAccessScreen(ScreenEnum.PartnerBilling);
  const isOnBillingScreen = pathname === ScreenEnum.PartnerBilling;

  useEffect(() => {
    if (!canSee || isOnBillingScreen) {
      return;
    }

    PartnerBillingManager.payoutAccount().then(({ data, success }) => success && setPayoutState(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSee, isOnBillingScreen]);

  const account = payoutState?.payout_account;

  if (!canSee || isOnBillingScreen || payoutState?.funds_custodian !== 'platform' || account?.is_ready_for_transfers) {
    return null;
  }

  const missing = describePayoutRequirements(account?.currently_due);
  const onlyBankAccountMissing = missing.length === 1 && missing[0] === PayoutRequirement.BankAccount;

  function message() {
    if (!account) {
      return t('not_started');
    }

    return onlyBankAccountMissing ? t('bank_account_missing') : t('incomplete');
  }

  return (
    <Alert
      banner
      type="warning"
      data-testid="partner-payout-account-banner"
      title={
        <div className="flex items-center justify-between">
          <span>{message()}</span>
          <Button type="link" onClick={() => history.push(ScreenEnum.PartnerBilling)}>
            {onlyBankAccountMissing ? t('add_bank_account') : t('cta')}
          </Button>
        </div>
      }
    />
  );
}
